import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { Card } from 'react-bootstrap';
import GamePageAd from "../ad/gamePageAd";
import GameTags from "../gameTag/gameTag";
import { useLocation, useParams } from 'react-router-dom';
import GameCard from "../gamecard/gameCard.jsx";
import Loader from "../Loader/loader";
import AllGames from "../../data/gameData.json";
import { Expand, Shrink } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { isFavorite, toggleFavorite } from "../utils/favorites";
import { getThemeClasses } from "../utils/themeClass";

const Game = () => {
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState({});
  const [similarGames, setsimilarGames] = useState([]);
  const [selectedGameUrl, setSelectedGameUrl] = useState('');
  const [iframeloading, setIFrameLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileFullPage, setMobileFullPage] = useState(false);
  const [isFav, setIsFav] = useState(false);

  const { theme } = useTheme();
  const themeClass = getThemeClasses(theme);

  const { gameId } = useParams();
  const location = useLocation();
  const { game } = location.state || {};

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.gtag('event', 'conversion_event_default', {});

    setSelectedGameUrl('');
    setMobileFullPage(false);
    if (!game) {
      fetchGameDetails(gameId);
    } else {
      if (typeof game.tags === 'string') {
        game.tags = game.tags.split(",");
      }
      fetchSimilarGames(game.category);
      setGameData(game);
      setLoading(false);
      setIsFav(isFavorite(game.id));
    }
  }, [game, gameId]);

  // (your useEffects unchanged)

  const toggleFullscreen = () => {
    const iframeContainer = document.getElementById("iframe-container");
    if (!iframeContainer) return;

    if (window.innerWidth < 768) {
      setMobileFullPage(true);
    }
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const request = iframeContainer.requestFullscreen || iframeContainer.webkitRequestFullscreen;
      request?.call(iframeContainer);
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      exit?.call(document).catch(() => {
        setMobileFullPage(false);
      });
    }
  };

  const handleGameonClick = (url) => () => {
    setSelectedGameUrl(url);
    setIFrameLoading(true);
    if (window.innerWidth < 768) {
      setMobileFullPage(true);
    }
  };

  const fetchGameDetails = async (id) => {
    try {
      const allGamesData = AllGames.data;
      const game = allGamesData.find(g => g.slug === id);
      if (!game) {
        fetchGameDetails("jumpycat");
      }
      if (typeof game?.tags === 'string') {
        game.tags = game.tags.split(",");
      }
      setGameData(game);
      fetchSimilarGames(game?.category);
      setIsFav(isFavorite(game.id));
      setLoading(false);
    } catch (error) {
      fetchGameDetails("jumpycat");
      console.error("Failed to fetch data:", error);
    }
  };

  const handleToggleFavorite = () => {
    const allGamesData = AllGames.data;
    const game = allGamesData.find(g => g.slug === gameData.slug);
    const updatedState = toggleFavorite(game.id);
    setIsFav(updatedState);
  };

  const fetchSimilarGames = async (category) => {
    let categoryDetails = AllGames.data.filter(cat => cat.category === category);
    categoryDetails = categoryDetails.slice(0, 12);
    setsimilarGames(categoryDetails);
  };

  const getResponsiveIframeStyle = () => {
    const isMobile = window.innerWidth < 768;
    if (mobileFullPage || isFullscreen || isMobile) {
      return {
        width: '100vw',
        height: '100dvh',
        maxHeight: '100dvh',
        aspectRatio: gameData.width && gameData.height ? `${gameData.width} / ${gameData.height}` : '16 / 9',
        backgroundColor: 'black',
      };
    }
    return {
      width: '100%',
      maxWidth: gameData.width || 960,
      maxHeight: 432,
      aspectRatio: gameData.width && gameData.height ? `${gameData.width} / ${gameData.height}` : '16 / 9',
    };
  };

  if (loading) {
    return (
      <div className={`flex justify-center items-center min-h-screen ${themeClass.bgColor} ${themeClass.baseText}`}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={`${themeClass.bgColor} ${themeClass.baseText} min-h-screen`}>

    <div className={`max-w-6xl mx-auto ${mobileFullPage ? 'p-0' : 'px-4 sm:px-6 py-8'} ${themeClass.bgColor} ${themeClass.baseText} flex flex-col items-center`}>
      <Helmet>
        <title>{gameData?.title} | BrowserArena</title>
        <meta name="description" content={`Play ${gameData?.title} for free online. ${gameData?.description?.slice(0, 140)}...`} />
        <link rel="canonical" href={`https://browserarena.com/games/${gameData?.slug}`} />
      </Helmet>

      {!selectedGameUrl && (
        <Card className={`relative game-card cursor-pointer mx-auto w-full max-w-md shadow-xl ${themeClass.overlayBg}`} onClick={handleGameonClick(gameData.url)}>
          <Card.Img variant="top" src={gameData.thumb} className="w-full object-cover rounded-lg" loading="eager" fetchpriority="high" />
          <div className="absolute inset-0 flex items-center justify-center">
            <button className={`px-6 py-2 text-lg font-bold rounded-full shadow-lg transition ${themeClass.exploreBtn}`}>▶ Play</button>
          </div>
        </Card>
      )}

      {selectedGameUrl && (
        <div id="iframe-container" className={`${mobileFullPage ? 'fixed inset-0 z-50 bg-black flex items-center justify-center w-screen h-[100dvh]' : 'relative w-full'}`}>
          {mobileFullPage && (
            <button onClick={() => setMobileFullPage(false)} className="absolute top-[env(safe-area-inset-top)] right-4 bg-pink-600 text-white px-4 py-2 rounded-full shadow-lg z-50">✖ Exit</button>
          )}

          {!mobileFullPage && (
            <button onClick={toggleFullscreen} className="absolute bottom-[env(safe-area-inset-bottom)] right-3 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition z-10">
              {isFullscreen ? <Shrink size={20} /> : <Expand size={20} />}
            </button>
          )}

          {iframeloading && (
            <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center z-40 rounded-md">
              <Loader />
            </div>
          )}

          <div className="relative w-full flex justify-center items-center" style={getResponsiveIframeStyle()}>
            <iframe title={gameData.title} src={gameData.url} allowFullScreen className="w-full h-full rounded-md" onLoad={() => setIFrameLoading(false)} />
          </div>
        </div>
      )}

      {!mobileFullPage && (
        <>
          <div className="text-center mt-8 px-2 sm:px-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold mb-4">{gameData.title}</h1>
            <GameTags tags={gameData.tags} />
            <div className="mt-3">
              <button onClick={handleToggleFavorite} className={`px-4 py-2 rounded-full transition shadow-md ${isFav ? "bg-pink-600 hover:bg-pink-500 text-white" : "bg-white text-pink-600 hover:bg-gray-200"}`}>
                {isFav ? "💖 Favorited" : "🤍 Add to Favorites"}
              </button>
            </div>
            <p className="mt-2 text-sm sm:text-base">🎮 <strong>Instructions:</strong> {gameData.instructions}</p>
            <p className="mt-2 text-sm sm:text-base">📜 <strong>Description:</strong> {gameData.description}</p>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold mt-8">🎥 Walkthrough</h2>
          <div id="gamemonetize-video" className="w-full mt-4 px-2" />

          <h2 className="text-xl sm:text-2xl font-bold mt-10 mb-4 text-center">🕹️ Similar Games</h2>
          <div className="grid grid-cols-3 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 md:gap-5">
            {similarGames.map((game, index) =>
              game.id !== gameData.id && (
                game.title === "ad"
                  ? <GamePageAd key={index} />
                  : <GameCard key={index} game={game} />
              )
            )}
          </div>
        </>
      )}
    </div>
    </div>
  );
};

export default Game;
